/**
 * Applying a Markdown-mode edit to a section (spec §8.19): parse the text, then replace only the range
 * where the new document differs, through a ProseMirror view bound to the Loro container. The binding
 * writes that step with its text diff, so untouched paragraphs keep their Loro containers, cursors and
 * annotations, and concurrent edits elsewhere merge (spike S14).
 */
import type { Node } from "prosemirror-model";
import type { EditorView } from "prosemirror-view";
import { fromMarkdown } from "./markdown";

/** The smallest replace turning `from` into `to`, or null when they are equal. */
export function minimalReplace(from: Node, to: Node): { start: number; end: number; slice: ReturnType<Node["slice"]> } | null {
  const start = from.content.findDiffStart(to.content);
  if (start == null) return null;
  const diffEnd = from.content.findDiffEnd(to.content);
  let endA = diffEnd?.a ?? from.content.size;
  let endB = diffEnd?.b ?? to.content.size;
  // findDiffEnd may overlap findDiffStart when the change repeats nearby content
  const overlap = start - Math.min(endA, endB);
  if (overlap > 0) {
    endA += overlap;
    endB += overlap;
  }
  return { start, end: endA, slice: to.slice(start, endB) };
}

/** Parse `markdown` and dispatch the minimal change into `view`. Returns whether anything changed. */
export function applyMarkdown(view: EditorView, markdown: string): boolean {
  const next = fromMarkdown(markdown);
  const change = minimalReplace(view.state.doc, next);
  if (!change) return false;
  view.dispatch(view.state.tr.replace(change.start, change.end, change.slice));
  return true;
}
